/* eslint-disable no-param-reassign */

import type { ProductsData } from '@/app/api/search/products/createProductsData.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation';
import { type SearchInfoDetailData } from '@/app/search/types.ts';
import { type Target } from '@/app/components/search/components/Form';
import type { StarTrack } from '@/app/common/starTrack/startrack.ts';
import type { CommonData } from '@/app/common/createCommonData.ts';
import { canUseDOM } from '@/app/common/utils/canUseDOM';
import { analyticsUserInteraction } from '../../../Analytics/analyticsUserInteraction';
import {
  Categories,
  normalizeEvents,
  normalizeFilterLabel,
  normalizeFilterValue,
  normalizeHorizontalFilter,
} from '../../../Analytics/utils';
import type { CuriosityParams, RouteType } from '../../../Curiosity';
import { applyResultPageFilterEvent, getFiltersFromQueryString } from '../utils';
import clickEvent from '../../../Curiosity/clickEvent';
import { getMaxPrice } from './Fields/getPriceField/priceUtils';
import type { Localization } from '../../../common/Localization';
import { addAirportsToLocalStorage } from '../HorizontalFilters/Pill/utils';
import FilterContainer from './FilterContainer';
import {
  clearListbox,
  getFieldNameFromFilterContainer,
  isCheckboxField,
  isListBoxField,
  isListBoxFieldContainer,
  updateFilter,
} from './utils';
import type { DynamicFiltersResponse } from '../../../utils/api/dynamicFiltersResponseTypes';
import type { HandleFiltersChanged } from '../../SearchPageContainer';
import getSearchParams from '../../../common/getSearchParams';
import { createSearchParamsAccommodationFacilities } from './Fields/getFacilitiesField/getFacilitiesFromQuery';

const verticalFiltersUpdateOnChange = (
  e: Target,
  appliedFilters: SearchParams,
  maxPriceSlider: number,
  commonData: CommonData,
  localization: Localization,
  curiosityParams: CuriosityParams,
  routeType: RouteType,
  searchInfoDetailData: SearchInfoDetailData,
  t: TranslationFunction,
  dynamicFiltersResponse?: DynamicFiltersResponse
): SearchParams => {
  const targetName = e.target.name;
  const targetValue = e.target.value;
  const targetClear = e.target.clear;

  const label = normalizeFilterLabel(e, appliedFilters, t);
  if (label) {
    analyticsUserInteraction({
      action: normalizeHorizontalFilter(targetName, e.target.value),
      category: Categories.resultsFilters,
      label,
      searchParams: getSearchParams(),
      searchInfoDetailData,
    });
  }
  const partialEventPayload = normalizeEvents(targetName);

  clickEvent({
    clickParams: {
      ...partialEventPayload,
      event_value: normalizeFilterValue(e),
      event_value_additional: 'vertical',
    },
    routeType,
    commonData,
    localization,
    curiosityParams,
  });

  if (targetName === URLParameters.CLIMATE) {
    if (targetValue) {
      appliedFilters[URLParameters.CLIMATE] = targetValue;
    } else {
      delete appliedFilters[URLParameters.CLIMATE];
    }
  }

  if (targetName === URLParameters.ACCOMODATION_NAME) {
    if (targetValue) {
      appliedFilters[URLParameters.ACCOMODATION_NAME] = targetValue;
    } else {
      delete appliedFilters[URLParameters.ACCOMODATION_NAME];
    }
  }

  if (targetName === URLParameters.PRE_FILTER_DESTINATION_AIRPORT) {
    if (targetValue && targetValue !== '0') {
      appliedFilters[URLParameters.PRE_FILTER_DESTINATION_AIRPORT] = targetValue;
    } else {
      delete appliedFilters[URLParameters.PRE_FILTER_DESTINATION_AIRPORT];
    }
  }

  if (targetName === URLParameters.OVERNIGHT_FLIGHTS) {
    if (targetValue && targetValue !== '0') {
      appliedFilters[URLParameters.OVERNIGHT_FLIGHTS] = targetValue;
    } else {
      delete appliedFilters[URLParameters.OVERNIGHT_FLIGHTS];
    }
  }

  if (targetName === URLParameters.PRICE) {
    const { min, max } = targetValue;
    if (canUseDOM) {
      const defaultMin = dynamicFiltersResponse?.price ? dynamicFiltersResponse.price.from : 0;
      const defaultMax = dynamicFiltersResponse?.price ? dynamicFiltersResponse.price.to : maxPriceSlider;
      const isDefault = max >= defaultMax && min <= defaultMin;

      if (isDefault) {
        delete appliedFilters[URLParameters.PRICE];
      } else {
        appliedFilters[URLParameters.PRICE] = `${min},${max}`;
      }
    }
  }

  if (isCheckboxField(targetName as URLParameters)) {
    if (targetValue) {
      appliedFilters[targetName] = '1';
    } else {
      delete appliedFilters[targetName];
    }
  }

  if (isListBoxField(targetName as URLParameters)) {
    updateFilter({
      targetName,
      targetValue,
      appliedFilters,
    });
  }

  if (isListBoxFieldContainer(targetName as FilterContainer) && targetClear) {
    const fieldName = getFieldNameFromFilterContainer(targetName as FilterContainer);
    clearListbox({ targetName: fieldName, appliedFilters });
  }

  if (targetName === FilterContainer.WaterwavesContainer && targetClear) {
    delete appliedFilters[URLParameters.WATER_WAVES];
  }

  appliedFilters = createSearchParamsAccommodationFacilities(appliedFilters);

  return appliedFilters;
};

type HandleVerticalFiltersUpdated = {
  e: Target;
  appliedFilters: SearchParams;
  commonData: CommonData;
  localization: Localization;
  curiosityParams: CuriosityParams;
  routeType: RouteType;
  searchParams: SearchParams;
  productsData: ProductsData;
  handleFiltersChanged: (params: HandleFiltersChanged) => void;
  starTrack: StarTrack;
  searchInfoDetailData: SearchInfoDetailData;
  t: TranslationFunction;
  dynamicFiltersResponse?: DynamicFiltersResponse;
};
export const handleVerticalFiltersUpdated = ({
  e,
  appliedFilters,
  commonData,
  localization,
  curiosityParams,
  routeType,
  searchParams,
  productsData,
  handleFiltersChanged,
  starTrack,
  searchInfoDetailData,
  t,
  dynamicFiltersResponse,
}: HandleVerticalFiltersUpdated) => {
  const { lang, currency } = localization;
  const maxPriceSlider = getMaxPrice(lang, currency);
  const oldFilters = getFiltersFromQueryString(searchParams);
  appliedFilters = verticalFiltersUpdateOnChange(
    e,
    appliedFilters,
    maxPriceSlider,
    commonData,
    localization,
    curiosityParams,
    routeType,
    searchInfoDetailData,
    t,
    dynamicFiltersResponse
  );
  addAirportsToLocalStorage({ e, productsData });

  applyResultPageFilterEvent({
    oldFilters,
    appliedFilters,
    searchParams,
    starTrack,
    localization,
  });

  const searchParamsFresh = getSearchParams();
  handleFiltersChanged({ searchParams: searchParamsFresh, appliedFilters });
};

export default verticalFiltersUpdateOnChange;
